import { useState } from 'react';
import { Save, X, Sparkles } from 'lucide-react';
import { DOCUMENT_CATEGORY_LABELS, type DocumentCategory, type MHRDocument } from '../../types/document';
import * as mhrApi from '../../lib/api';

interface DocumentMetadataEditorProps {
  document: MHRDocument;
  onSave: (updated: MHRDocument) => void;
  onCancel: () => void;
}

export function DocumentMetadataEditor({ document, onSave, onCancel }: DocumentMetadataEditorProps) {
  const [category, setCategory] = useState<DocumentCategory | ''>(document.document_category || '');
  const [documentDate, setDocumentDate] = useState(document.document_date || '');
  const [facilityName, setFacilityName] = useState(document.facility_name || '');
  const [description, setDescription] = useState(document.description || '');
  const [tags, setTags] = useState(document.tags?.join(', ') || '');
  const [saving, setSaving] = useState(false);

  const hasAiSuggestions = document.ai_document_category || document.ai_facility_name || document.ai_description || document.ai_document_date;

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await mhrApi.updateDocumentMetadata(document.id, {
        document_category: category ? category as DocumentCategory : undefined,
        document_date: documentDate || null,
        facility_name: facilityName || null,
        description: description || null,
        tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
      onSave(updated);
    } catch (error) {
      console.error('Failed to update document metadata:', error);
    } finally {
      setSaving(false);
    }
  }

  function applyAiSuggestion(field: 'category' | 'date' | 'facility' | 'description') {
    switch (field) {
      case 'category':
        if (document.ai_document_category) setCategory(document.ai_document_category as DocumentCategory);
        break;
      case 'date':
        if (document.ai_document_date) setDocumentDate(document.ai_document_date);
        break;
      case 'facility':
        if (document.ai_facility_name) setFacilityName(document.ai_facility_name);
        break;
      case 'description':
        if (document.ai_description) setDescription(document.ai_description);
        break;
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Edit Document Metadata</h4>
        {hasAiSuggestions && (
          <span className="inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400">
            <Sparkles size={12} />
            AI suggestions available
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category</label>
          <div className="flex gap-1">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory | '')}
              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select category...</option>
              {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {document.ai_document_category && !category && (
              <button
                onClick={() => applyAiSuggestion('category')}
                title={`AI suggestion: ${DOCUMENT_CATEGORY_LABELS[document.ai_document_category as DocumentCategory] || document.ai_document_category}`}
                className="p-1.5 text-purple-500 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
              >
                <Sparkles size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Date */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Document Date</label>
          <div className="flex gap-1">
            <input
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {document.ai_document_date && !documentDate && (
              <button
                onClick={() => applyAiSuggestion('date')}
                title={`AI suggestion: ${document.ai_document_date}`}
                className="p-1.5 text-purple-500 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
              >
                <Sparkles size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Facility */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Facility</label>
          <div className="flex gap-1">
            <input
              type="text"
              value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}
              placeholder={document.ai_facility_name || 'Facility name...'}
              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {document.ai_facility_name && !facilityName && (
              <button
                onClick={() => applyAiSuggestion('facility')}
                title={`AI suggestion: ${document.ai_facility_name}`}
                className="p-1.5 text-purple-500 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
              >
                <Sparkles size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tags</label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Comma-separated tags..."
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
        <div className="flex gap-1">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={document.ai_description || 'Brief description...'}
            rows={2}
            className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          {document.ai_description && !description && (
            <button
              onClick={() => applyAiSuggestion('description')}
              title="Apply AI suggestion"
              className="p-1.5 text-purple-500 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded self-start"
            >
              <Sparkles size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0"
        >
          <X size={14} className="inline mr-1" />
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          <Save size={14} className="inline mr-1" />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
